import ColorPicker from "@/pages/delays/categories/components/ColorPicker/ColorPicker";
import {
  useCommoditySubstitutesQuery,
  useUpsertCommodityWithSubstitutesMutation,
} from "@/pages/sy/hooks/queries/useCommoditiesQuery";
import { useCommodityTypesQuery } from "@/pages/sy/hooks/queries/useCommodityTypesQuery";
import { modalStyles } from "@/shared/styles/modalStyles";
import { ErrorModal, dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { CommentsField } from "../../../../../shared/fields/CommentsField";
import { COMMODITY_INITIAL_VALUES, Commodity } from "../../../models/Commodity";
import { SubstituteCommoditiesSelector } from "./SubstituteCommoditiesSelector";

type Props = {
  commodity: Commodity | null;
  modal: "new" | "edit" | "delete" | "";
  onHide: ({
    shouldUpdate,
    close,
    entityId,
  }: {
    shouldUpdate: boolean;
    close: boolean;
    entityId?: number;
  }) => void;
};

export const CommodityDetails = ({ commodity, modal, onHide }: Props) => {
  const [message, setNotificationMessage] = useState<string>("");
  const [error, setError] = useState("");
  const [substituteCommodityIds, setSubstituteCommodityIds] = useState<
    number[]
  >([]);

  const { data: existingSubstitutes = [], isLoading: isLoadingSubstitutes } =
    useCommoditySubstitutesQuery(commodity?.CommodityId ?? null, false, (err) =>
      setError(err.message),
    );

  const { data: commodityTypes = [] } = useCommodityTypesQuery(true, (err) =>
    setError(err.message),
  );

  const upsertMutation = useUpsertCommodityWithSubstitutesMutation();

  // Derive loading state from mutation
  const isSubmitting = upsertMutation.isPending;

  const {
    reset,
    getValues,
    handleSubmit,
    control,
    formState: { dirtyFields },
  } = useForm<Commodity>({
    mode: "onChange",
  });

  // Check if substitutes have been changed from the original
  const originalSubstituteIds = existingSubstitutes
    .map((s) => s.SubstituteCommodityId)
    .sort((a, b) => a - b);
  const currentSubstituteIds = [...substituteCommodityIds].sort(
    (a, b) => a - b,
  );
  const areSubstitutesDirty =
    originalSubstituteIds.length !== currentSubstituteIds.length ||
    originalSubstituteIds.some((id, i) => id !== currentSubstituteIds[i]);

  const isCommodityDirty = Object.keys(dirtyFields).length > 0;
  const isNewCommodity = modal === "new";
  const isFormDirty = isCommodityDirty || isNewCommodity || areSubstitutesDirty;

  const onSubmit: SubmitHandler<Commodity> = async (data) => {
    if (modal === "delete") return;

    try {
      const result = await upsertMutation.mutateAsync({
        commodity: {
          ...data,
          CommodityId: data.IsDuplicate ? null : data.CommodityId,
          CommodityName: data.CommodityName.trimEnd(),
        },
        substituteCommodityIds,
      });

      setNotificationMessage("The commodity was saved successfully.");
      onHide({
        shouldUpdate: true,
        close: false,
        entityId: result.entityId,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save commodity");
    }
  };

  // Only reset form when modal opens or when switching to a different commodity
  useEffect(() => {
    if (modal !== "") {
      reset(commodity || COMMODITY_INITIAL_VALUES);
      setError("");
    }
    if (modal === "new") {
      setSubstituteCommodityIds([]);
    }
  }, [modal, commodity?.CommodityId]);

  useEffect(() => {
    if (existingSubstitutes.length > 0) {
      setSubstituteCommodityIds(
        existingSubstitutes.map((s) => s.SubstituteCommodityId),
      );
    } else if (commodity?.CommodityId) {
      setSubstituteCommodityIds([]);
    }
  }, [existingSubstitutes, commodity?.CommodityId]);

  return (
    <>
      {commodity === null && modal !== "new" ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography>Select a commodity or create a new one</Typography>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} style={{ padding: "20px" }}>
            {/* Primera fila: Name y Author */}
            <Grid item xs={12} md={7}>
              <Controller
                name="CommodityName"
                control={control}
                rules={{ required: "Commodity name is required" }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Name"
                    variant="outlined"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    size="small"
                    fullWidth
                    error={!!error}
                    helperText={error?.message}
                    disabled={modal === "delete"}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Controller
                name="Author"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Author"
                    variant="outlined"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    size="small"
                    fullWidth
                    disabled={modal === "delete"}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Controller
                name="CommodityTypeId"
                control={control}
                rules={{ required: "Commodity type is required" }}
                render={({ field, fieldState: { error } }) => (
                  <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={!!error}
                  >
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      label="Type"
                      disabled={modal === "delete"}
                    >
                      {commodityTypes.map((type) => (
                        <MenuItem
                          key={type.CommodityTypeId ?? ""}
                          value={type.CommodityTypeId ?? ""}
                        >
                          {type.CommodityTypeName}
                        </MenuItem>
                      ))}
                    </Select>
                    {error && (
                      <Box component="span" sx={modalStyles.errorLabel}>
                        {error?.message}
                      </Box>
                    )}
                  </FormControl>
                )}
              />
            </Grid>
            <Grid
              item
              xs={4}
              md={4}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Controller
                name="CommodityColor"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <div style={{ marginTop: "10px" }}>
                    <ColorPicker
                      onChange={(_: string, color: string) =>
                        field.onChange(color)
                      }
                      color={field.value ?? ""}
                      disabled={modal === "delete"}
                      label="Color"
                    />
                    {error && (
                      <Box component="span" sx={modalStyles.errorLabel}>
                        {error?.message}
                      </Box>
                    )}
                  </div>
                )}
              />
            </Grid>
            <Grid
              item
              xs={3}
              md={3}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Controller
                name="IsEnable"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(field.value)}
                        onChange={(e) => field.onChange(e.target.checked)}
                        color="primary"
                        disabled={modal === "delete"}
                      />
                    }
                    label="Enabled"
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={5}
              md={5}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Controller
                name="UpdateTimestamp"
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Updated Time"
                    variant="outlined"
                    value={
                      field.value
                        ? (dxtToLocalServerTime(
                            field.value,
                            "MM/dd/yy HH:mm",
                          ) ?? "")
                        : ""
                    }
                    onChange={field.onChange}
                    size="small"
                    fullWidth
                    disabled
                    style={{ display: field.value ? "block" : "none" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <SubstituteCommoditiesSelector
                commodityId={commodity?.CommodityId || null}
                value={substituteCommodityIds}
                onChange={setSubstituteCommodityIds}
                disabled={modal === "delete" || isLoadingSubstitutes}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="Comments"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <CommentsField
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    fieldError={error}
                    getValues={getValues}
                    disabled={modal === "delete"}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={4} style={{ marginLeft: "auto" }}>
              <Button
                fullWidth
                variant="contained"
                color="inherit"
                onClick={() => onHide({ shouldUpdate: false, close: true })}
              >
                CLOSE
              </Button>
            </Grid>
            <Grid item xs={6} md={4}>
              <Button
                fullWidth
                startIcon={isSubmitting && <CircularProgress size="1rem" />}
                disabled={isSubmitting || !isFormDirty || modal === "delete"}
                variant="contained"
                color="primary"
                type="submit"
              >
                SAVE
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
      <NotificationSnackBar
        message={message}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
